import { FormDefinition } from '../types/forms';
import { v4 as uuidv4 } from 'uuid';

export const LOAN_APPLICATION_TEMPLATE: FormDefinition = {
  name: "Small Business Loan Application",
  description: "Comprehensive loan application form for small business lending with automated underwriting integration",
  category: "Loan Application",
  isActive: true,
  fields: [
    // Business Information Section
    {
      id: "section_business_info",
      type: "section",
      label: "Business Information",
      order: 0,
      width: "full"
    },
    {
      id: "business_name",
      type: "text",
      label: "Legal Business Name",
      placeholder: "Enter your legal business name",
      helpText: "The official name as registered with the state",
      required: true,
      order: 1,
      width: "half",
      validation: {
        minLength: 2,
        maxLength: 200
      }
    },
    {
      id: "dba_name",
      type: "text",
      label: "DBA Name (if different)",
      placeholder: "Doing business as...",
      order: 2,
      width: "half",
      validation: {
        maxLength: 200
      }
    },
    {
      id: "ein",
      type: "text",
      label: "Employer Identification Number (EIN)",
      placeholder: "XX-XXXXXXX",
      helpText: "9-digit federal tax identification number",
      required: true,
      order: 3,
      width: "half",
      validation: {
        pattern: "^\\d{2}-\\d{7}$"
      }
    },
    {
      id: "business_type",
      type: "select",
      label: "Business Structure",
      required: true,
      order: 4,
      width: "half",
      options: [
        { value: "LLC", label: "LLC (Limited Liability Company)" },
        { value: "Corporation", label: "Corporation" },
        { value: "Partnership", label: "Partnership" },
        { value: "Sole Proprietorship", label: "Sole Proprietorship" },
        { value: "Other", label: "Other" }
      ]
    },
    {
      id: "industry",
      type: "select",
      label: "Industry",
      required: true,
      order: 5,
      width: "half",
      options: [
        { value: "retail", label: "Retail" },
        { value: "restaurants", label: "Restaurants & Food Service" },
        { value: "construction", label: "Construction" },
        { value: "healthcare", label: "Healthcare" },
        { value: "professional_services", label: "Professional Services" },
        { value: "manufacturing", label: "Manufacturing" },
        { value: "technology", label: "Technology" },
        { value: "transportation", label: "Transportation" },
        { value: "real_estate", label: "Real Estate" },
        { value: "agriculture", label: "Agriculture" },
        { value: "other", label: "Other" }
      ]
    },
    {
      id: "year_established",
      type: "number",
      label: "Year Established",
      required: true,
      order: 6,
      width: "half",
      validation: {
        min: 1800,
        max: 2024
      }
    },
    {
      id: "num_employees",
      type: "number",
      label: "Number of Employees",
      required: true,
      order: 7,
      width: "half",
      validation: {
        min: 0
      }
    },
    {
      id: "annual_revenue",
      type: "currency",
      label: "Annual Revenue",
      placeholder: "Enter annual revenue",
      helpText: "Most recent 12-month revenue",
      required: true,
      order: 8,
      width: "half",
      validation: {
        min: 0
      }
    },

    // Business Address Section
    {
      id: "section_address",
      type: "section",
      label: "Business Address",
      order: 9,
      width: "full"
    },
    {
      id: "address_street",
      type: "text",
      label: "Street Address",
      required: true,
      order: 10,
      width: "full",
      validation: {
        maxLength: 200
      }
    },
    {
      id: "address_city",
      type: "text",
      label: "City",
      required: true,
      order: 11,
      width: "third",
      validation: {
        maxLength: 100
      }
    },
    {
      id: "address_state",
      type: "select",
      label: "State",
      required: true,
      order: 12,
      width: "third",
      options: [
        { value: "AL", label: "Alabama" },
        { value: "AK", label: "Alaska" },
        { value: "AZ", label: "Arizona" },
        { value: "AR", label: "Arkansas" },
        { value: "CA", label: "California" },
        { value: "CO", label: "Colorado" },
        { value: "CT", label: "Connecticut" },
        { value: "DE", label: "Delaware" },
        { value: "FL", label: "Florida" },
        { value: "GA", label: "Georgia" },
        { value: "HI", label: "Hawaii" },
        { value: "ID", label: "Idaho" },
        { value: "IL", label: "Illinois" },
        { value: "IN", label: "Indiana" },
        { value: "IA", label: "Iowa" },
        { value: "KS", label: "Kansas" },
        { value: "KY", label: "Kentucky" },
        { value: "LA", label: "Louisiana" },
        { value: "ME", label: "Maine" },
        { value: "MD", label: "Maryland" },
        { value: "MA", label: "Massachusetts" },
        { value: "MI", label: "Michigan" },
        { value: "MN", label: "Minnesota" },
        { value: "MS", label: "Mississippi" },
        { value: "MO", label: "Missouri" },
        { value: "MT", label: "Montana" },
        { value: "NE", label: "Nebraska" },
        { value: "NV", label: "Nevada" },
        { value: "NH", label: "New Hampshire" },
        { value: "NJ", label: "New Jersey" },
        { value: "NM", label: "New Mexico" },
        { value: "NY", label: "New York" },
        { value: "NC", label: "North Carolina" },
        { value: "ND", label: "North Dakota" },
        { value: "OH", label: "Ohio" },
        { value: "OK", label: "Oklahoma" },
        { value: "OR", label: "Oregon" },
        { value: "PA", label: "Pennsylvania" },
        { value: "RI", label: "Rhode Island" },
        { value: "SC", label: "South Carolina" },
        { value: "SD", label: "South Dakota" },
        { value: "TN", label: "Tennessee" },
        { value: "TX", label: "Texas" },
        { value: "UT", label: "Utah" },
        { value: "VT", label: "Vermont" },
        { value: "VA", label: "Virginia" },
        { value: "WA", label: "Washington" },
        { value: "WV", label: "West Virginia" },
        { value: "WI", label: "Wisconsin" },
        { value: "WY", label: "Wyoming" }
      ]
    },
    {
      id: "address_zip",
      type: "text",
      label: "ZIP Code",
      required: true,
      order: 13,
      width: "third",
      validation: {
        pattern: "^\\d{5}(-\\d{4})?$"
      }
    },

    // Contact Information Section
    {
      id: "section_contact",
      type: "section",
      label: "Primary Contact Information",
      order: 14,
      width: "full"
    },
    {
      id: "contact_name",
      type: "text",
      label: "Primary Contact Name",
      required: true,
      order: 15,
      width: "half",
      validation: {
        maxLength: 100
      }
    },
    {
      id: "contact_title",
      type: "text",
      label: "Title",
      order: 16,
      width: "half",
      validation: {
        maxLength: 100
      }
    },
    {
      id: "contact_phone",
      type: "phone",
      label: "Phone Number",
      required: true,
      order: 17,
      width: "half"
    },
    {
      id: "contact_email",
      type: "email",
      label: "Email Address",
      required: true,
      order: 18,
      width: "half"
    },
    {
      id: "website",
      type: "text",
      label: "Website",
      placeholder: "https://www.example.com",
      order: 19,
      width: "half"
    },

    // Loan Request Section
    {
      id: "section_loan_request",
      type: "section",
      label: "Loan Request Details",
      order: 20,
      width: "full"
    },
    {
      id: "loan_type",
      type: "select",
      label: "Type of Loan",
      required: true,
      order: 21,
      width: "half",
      options: [
        { value: "Term Loan", label: "Term Loan" },
        { value: "Line of Credit", label: "Line of Credit" },
        { value: "Equipment Financing", label: "Equipment Financing" },
        { value: "SBA Loan", label: "SBA Loan" },
        { value: "Merchant Cash Advance", label: "Merchant Cash Advance" },
        { value: "Invoice Factoring", label: "Invoice Factoring" },
        { value: "Real Estate Loan", label: "Real Estate Loan" }
      ]
    },
    {
      id: "loan_purpose",
      type: "select",
      label: "Purpose of Loan",
      required: true,
      order: 22,
      width: "half",
      options: [
        { value: "Working Capital", label: "Working Capital" },
        { value: "Equipment Purchase", label: "Equipment Purchase" },
        { value: "Real Estate", label: "Real Estate" },
        { value: "Inventory", label: "Inventory" },
        { value: "Business Expansion", label: "Business Expansion" },
        { value: "Debt Consolidation", label: "Debt Consolidation" },
        { value: "Other", label: "Other" }
      ]
    },
    {
      id: "requested_amount",
      type: "currency",
      label: "Requested Loan Amount",
      required: true,
      order: 23,
      width: "half",
      validation: {
        min: 1000,
        max: 10000000
      }
    },
    {
      id: "term_months",
      type: "number",
      label: "Desired Term (Months)",
      required: true,
      order: 24,
      width: "half",
      validation: {
        min: 1,
        max: 360
      }
    },
    {
      id: "loan_purpose_details",
      type: "textarea",
      label: "Detailed Description of Loan Purpose",
      placeholder: "Please provide specific details about how the loan will be used...",
      required: true,
      order: 25,
      width: "full",
      validation: {
        minLength: 50,
        maxLength: 1000
      }
    },

    // Financial Information Section
    {
      id: "section_financial",
      type: "section",
      label: "Financial Information",
      order: 26,
      width: "full"
    },
    {
      id: "monthly_revenue",
      type: "currency",
      label: "Average Monthly Revenue",
      helpText: "Average over the last 12 months",
      required: true,
      order: 27,
      width: "half",
      validation: {
        min: 0
      }
    },
    {
      id: "monthly_expenses",
      type: "currency",
      label: "Average Monthly Expenses",
      helpText: "Average over the last 12 months",
      required: true,
      order: 28,
      width: "half",
      validation: {
        min: 0
      }
    },
    {
      id: "credit_score",
      type: "number",
      label: "Business Credit Score (if known)",
      helpText: "Leave blank if unknown",
      order: 29,
      width: "half",
      validation: {
        min: 300,
        max: 850
      }
    },
    {
      id: "personal_credit_score",
      type: "number",
      label: "Personal Credit Score (Owner)",
      helpText: "Personal credit score of primary owner",
      required: true,
      order: 30,
      width: "half",
      validation: {
        min: 300,
        max: 850
      }
    },
    {
      id: "existing_debt",
      type: "currency",
      label: "Total Existing Business Debt",
      helpText: "Include all outstanding business loans and credit lines",
      required: true,
      order: 31,
      width: "half",
      validation: {
        min: 0
      }
    },
    {
      id: "cash_flow",
      type: "currency",
      label: "Monthly Cash Flow",
      helpText: "Average monthly income minus expenses",
      required: true,
      order: 32,
      width: "half"
    },

    // Collateral Section
    {
      id: "section_collateral",
      type: "section",
      label: "Collateral Information",
      order: 33,
      width: "full"
    },
    {
      id: "collateral_offered",
      type: "radio",
      label: "Are you offering collateral for this loan?",
      required: true,
      order: 34,
      width: "full",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" }
      ]
    },
    {
      id: "collateral_type",
      type: "select",
      label: "Type of Collateral",
      order: 35,
      width: "half",
      conditional: {
        fieldId: "collateral_offered",
        operator: "equals",
        value: "yes"
      },
      options: [
        { value: "Real Estate", label: "Real Estate" },
        { value: "Equipment", label: "Equipment" },
        { value: "Inventory", label: "Inventory" },
        { value: "Accounts Receivable", label: "Accounts Receivable" },
        { value: "Personal Guarantee", label: "Personal Guarantee" }
      ]
    },
    {
      id: "collateral_value",
      type: "currency",
      label: "Estimated Collateral Value",
      order: 36,
      width: "half",
      conditional: {
        fieldId: "collateral_offered",
        operator: "equals",
        value: "yes"
      },
      validation: {
        min: 0
      }
    },
    {
      id: "collateral_description",
      type: "textarea",
      label: "Collateral Description",
      placeholder: "Provide detailed description of the collateral being offered...",
      order: 37,
      width: "full",
      conditional: {
        fieldId: "collateral_offered",
        operator: "equals",
        value: "yes"
      },
      validation: {
        maxLength: 500
      }
    },

    // Additional Information Section
    {
      id: "section_additional",
      type: "section",
      label: "Additional Information",
      order: 38,
      width: "full"
    },
    {
      id: "bankruptcy_history",
      type: "radio",
      label: "Has the business or owner(s) ever filed for bankruptcy?",
      required: true,
      order: 39,
      width: "full",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" }
      ]
    },
    {
      id: "litigation_history",
      type: "radio",
      label: "Is the business currently involved in any litigation?",
      required: true,
      order: 40,
      width: "full",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" }
      ]
    },
    {
      id: "additional_notes",
      type: "textarea",
      label: "Additional Comments or Information",
      placeholder: "Please provide any additional information that might be relevant to your loan application...",
      order: 41,
      width: "full",
      validation: {
        maxLength: 1000
      }
    },

    // Document Upload Section
    {
      id: "section_documents",
      type: "section",
      label: "Required Documents",
      order: 42,
      width: "full"
    },
    {
      id: "doc_financial_statements",
      type: "file",
      label: "Financial Statements (Last 2 Years)",
      helpText: "Upload profit & loss statements and balance sheets",
      required: true,
      order: 43,
      width: "half"
    },
    {
      id: "doc_tax_returns",
      type: "file",
      label: "Business Tax Returns (Last 2 Years)",
      required: true,
      order: 44,
      width: "half"
    },
    {
      id: "doc_bank_statements",
      type: "file",
      label: "Bank Statements (Last 3 Months)",
      required: true,
      order: 45,
      width: "half"
    },
    {
      id: "doc_articles_incorporation",
      type: "file",
      label: "Articles of Incorporation / Operating Agreement",
      required: true,
      order: 46,
      width: "half"
    },

    // Agreement Section
    {
      id: "section_agreement",
      type: "section",
      label: "Agreement and Signature",
      order: 47,
      width: "full"
    },
    {
      id: "terms_agreement",
      type: "checkbox",
      label: "I agree to the terms and conditions",
      helpText: "By checking this box, I certify that all information provided is true and accurate to the best of my knowledge.",
      required: true,
      order: 48,
      width: "full",
      options: [
        { value: "agreed", label: "I agree to the terms and conditions and authorize the lender to verify the information provided." }
      ]
    },
    {
      id: "electronic_signature",
      type: "signature",
      label: "Electronic Signature",
      helpText: "Please provide your electronic signature",
      required: true,
      order: 49,
      width: "full"
    }
  ],
  styling: {
    theme: "default",
    layout: "single-column"
  },
  notifications: {
    onSubmit: [
      {
        type: "email",
        recipient: "underwriting@lender.com",
        template: "loan_application_submitted"
      },
      {
        type: "internal",
        recipient: "loan_processing_team",
        template: "new_application_notification"
      }
    ]
  },
  id: uuidv4(),
  version: '',
  createdAt: '',
  updatedAt: '',
  createdBy: ''
};

